import { Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import {
  PILL_FILLED_CLASS,
  PILL_OUTLINE_CLASS,
} from "./CancelSubscriptionDialog";
import {
  CANCEL_RIGHT_AFTER_LINE,
  OR_DIVIDER,
  cancelTodayLabel,
  deliverySavingLine,
  noFeeLine,
  saveLabel,
  totalSavingLine,
  waitHeading,
} from "./cancel-after-delivery-copy";
import { formatPrice } from "./formatters";
import type { ManageSubscriptionSheetProduct } from "./types";

export interface BeforeYouCancelBodyProps {
  product: ManageSubscriptionSheetProduct;
  onSwitchToOneTime: () => void;
  onKeepSubscription: () => void;
  submitPending?: boolean;
  onCancelAfterNextDelivery?: () => void;
  nextBillingDate?: string | null;
}

export function BeforeYouCancelBody({
  product,
  onSwitchToOneTime,
  onKeepSubscription,
  submitPending = false,
  onCancelAfterNextDelivery,
  nextBillingDate,
}: BeforeYouCancelBodyProps) {
  const paidAmount =
    product.subscription_unit_price != null &&
      Number.isFinite(product.subscription_unit_price)
      ? product.subscription_unit_price
      : null;
  const oneTimeAmount =
    product.subscription_one_time_price != null &&
      Number.isFinite(product.subscription_one_time_price)
      ? product.subscription_one_time_price
      : null;

  const diff =
    paidAmount != null && oneTimeAmount != null
      ? Math.max(0, oneTimeAmount - paidAmount)
      : 0;

  const switchLabel =
    diff > 0
      ? `Switch to one-time (+$${formatPrice(diff)})`
      : "Switch to one-time";

  const showSave =
    onCancelAfterNextDelivery != null && diff > 0 && !!nextBillingDate;

  if (showSave && paidAmount != null && nextBillingDate) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-5 tw:px-2 tw:text-[#523D33]">
        <div className="tw:flex tw:flex-col tw:gap-3">
          <p className="tw:text-base tw:font-medium tw:text-center tw:m-0 tw:mx-auto tw:max-w-[25ch]">
            Got it - we can switch your order to a one-time purchase.
          </p>
          <p className="tw:text-sm tw:leading-relaxed tw:m-0 tw:text-[#523D33]/80">
            Subscription prices are designed for customers with at least 2
            deliveries. Switching your order to One-time updates the price to
            match.
          </p>
        </div>

        {/* The new option comes first so it's visible without scrolling. */}
        <section className="tw:flex tw:flex-col tw:gap-3 tw:rounded-xl tw:bg-[#FDEDEE] tw:p-4">
          <p className="tw:m-0 tw:text-base tw:font-semibold">
            {waitHeading(nextBillingDate)}
          </p>
          <ul className="tw:m-0 tw:p-0 tw:list-none tw:flex tw:flex-col tw:gap-1.5 tw:text-sm">
            {[
              noFeeLine(diff),
              deliverySavingLine(diff, nextBillingDate),
              CANCEL_RIGHT_AFTER_LINE,
            ].map((line) => (
              <li key={line} className="tw:flex tw:items-start tw:gap-2">
                <Check className="tw:size-4 tw:shrink-0 tw:mt-0.5" aria-hidden />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="tw:m-0 tw:pt-2 tw:border-t tw:border-[#523D33]/15 tw:text-base tw:font-semibold">
            {totalSavingLine(diff)}
          </p>
          <Button
            type="button"
            disabled={submitPending}
            onClick={onCancelAfterNextDelivery}
            // Long labels wrap rather than overflow the pill on phones.
            className={cn(
              PILL_FILLED_CLASS,
              "tw:h-auto tw:min-h-10 tw:whitespace-normal tw:py-2.5 tw:leading-snug tw:bg-[#FAC8CA]",
            )}
          >
            {submitPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              saveLabel(nextBillingDate)
            )}
          </Button>
        </section>

        <div className="tw:flex tw:items-center tw:gap-3 tw:text-xs tw:uppercase tw:tracking-wide tw:text-[#523D33]/60">
          <span className="tw:h-px tw:flex-1 tw:bg-[#523D33]/15" />
          {OR_DIVIDER}
          <span className="tw:h-px tw:flex-1 tw:bg-[#523D33]/15" />
        </div>

        {/* Tor's price lines describe cancelling today, so its button sits right under them. */}
        <div className="tw:flex tw:flex-col tw:gap-3">
          <dl className="tw:m-0 tw:grid tw:grid-cols-[1fr_auto] tw:gap-x-4 tw:gap-y-1 tw:text-sm">
            <dt className="tw:text-[#523D33]/80">Subscription price</dt>
            <dd className="tw:m-0 tw:text-right">${formatPrice(paidAmount)}</dd>
            <dt className="tw:text-[#523D33]/80">One-time price</dt>
            <dd className="tw:m-0 tw:text-right">${formatPrice(oneTimeAmount ?? 0)}</dd>
            <div className="tw:col-span-2 tw:my-1 tw:border-t tw:border-[#523D33]/15" />
            <dt className="tw:font-medium">Price difference</dt>
            <dd className="tw:m-0 tw:text-right tw:font-medium">
              ${formatPrice(diff)}
            </dd>
          </dl>
          <Button
            type="button"
            variant="outline"
            disabled={submitPending}
            onClick={onSwitchToOneTime}
            className={PILL_OUTLINE_CLASS}
          >
            {cancelTodayLabel(diff)}
          </Button>
        </div>

        <div className="tw:flex tw:flex-col tw:gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={submitPending}
            onClick={onKeepSubscription}
            className={PILL_OUTLINE_CLASS}
          >
            Keep subscription
          </Button>
          <p className="tw:text-xs tw:text-center tw:m-0 tw:text-[#523D33]/70">
            You can skip, pause or cancel in seconds - anytime.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:px-2">
      <div className="tw:flex tw:justify-center tw:items-center">
        <p className="tw:text-base tw:font-medium tw:text-center tw:m-0 tw:max-w-[25ch]">
          Got it - we can switch your order to a one-time purchase.
        </p>
      </div>
      <p className="tw:text-base tw:text-left tw:m-0 tw:pb-4">
        Subscription prices are designed for customers with at least 2
        deliveries. Switching your order to One-time updates the price to match.
      </p>

      {paidAmount != null && oneTimeAmount != null && oneTimeAmount > 0 && (
        <div className="tw:flex tw:flex-col tw:gap-6 tw:text-base tw:max-w-[30ch]">
          <div className="tw:flex tw:flex-col tw:gap-0.5">
            <p className="tw:m-0">
              <span className="tw:font-medium">Subscription price:</span> $
              {formatPrice(paidAmount)}
            </p>
            <p className="tw:m-0">
              <span className="tw:font-medium">One-time price:</span> $
              {formatPrice(oneTimeAmount)}
            </p>
          </div>
          <p className="tw:m-0">
            <span className="tw:font-medium">Price difference:</span> $
            {formatPrice(diff)}
          </p>
        </div>
      )}

      <div className="tw:flex tw:flex-col tw:gap-4 tw:pt-1">
        <Button
          type="button"
          disabled={submitPending}
          onClick={onSwitchToOneTime}
          className={PILL_FILLED_CLASS}
        >
          {submitPending ? (
            <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
          ) : (
            switchLabel
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={submitPending}
          onClick={onKeepSubscription}
          className={PILL_OUTLINE_CLASS}
        >
          Keep subscription
        </Button>
      </div>

      <p className="tw:text-sm tw:text-center tw:m-0 tw:px-2">
        You can skip, pause or cancel in seconds - anytime.
      </p>
    </div>
  );
}
